import {
  Box,
  ClickAwayListener,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import {
  BCPAngleDown,
  BCPAngleUp,
  BCPShare,
  BCPSortAlphaDownAsc,
  BCPSortAmountDownAsc,
  BCPSortAmountDownDesc
} from "../../../components/icons/bcp/BCPIcons";
import useExportImage, {
  slugifyFilename
} from "../../../components/utilities/useExportImage";
import ShareImageModal from "../../../components/modals/ShareImageModal";
import { postNotification } from "../../../components/utilities/Notifications";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);

const getColor = (rank, total) => {
  const ratio = total > 1 ? rank / (total - 1) : 0;
  const l = 35 + ratio * 35;
  return `hsl(270, 65%, ${l}%)`;
};

const BarTrack = ({ children, theme, sx }) => (
  <Box
    sx={{
      height: { xs: 12 },
      borderRadius: 4,
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.1)"
          : "rgba(0,0,0,0.1)",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      ...sx
    }}
  >
    {children}
  </Box>
);

const SegmentHoverCard = ({ segment, dataType, color, theme }) => (
  <Box
    sx={{
      background: theme.palette.mode === "dark" ? "#1a202c" : "#ffffff",
      color: theme.palette.text.primary,
      padding: "10px 14px",
      borderRadius: 1,
      boxShadow: "0 3px 9px rgba(0,0,0,0.5)",
      minWidth: 160
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: 0.5,
          backgroundColor: color,
          flexShrink: 0
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {segment.label}
      </Typography>
    </Box>
    <Typography variant="body2">
      {dataType}: {segment.value}
    </Typography>
    <Typography variant="body2">{segment.percentage}%</Typography>
  </Box>
);

function SimpleRow({
  row,
  total,
  dataType,
  useMaxValue,
  maxValue,
  color,
  theme
}) {
  const { label, value } = row;
  const percentage = parseFloat(((value / total) * 100).toFixed(1));
  const barPercentage = useMaxValue
    ? parseFloat(((value / maxValue) * 100).toFixed(1))
    : percentage;
  return (
    <Box sx={{ mb: { xs: 2.5, sm: 2 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: { xs: 1, sm: 0.75 },
          flexWrap: "wrap"
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            textDecoration: "none"
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ({percentage}%) &bull; {value} {dataType}
        </Typography>
      </Box>
      <BarTrack theme={theme}>
        <Box
          sx={{
            height: "100%",
            width: `${barPercentage}%`,
            backgroundColor: color,
            borderRadius: 4,
            transition: "width 0.4s ease"
          }}
        />
      </BarTrack>
    </Box>
  );
}

function NestedRow({ row, total, dataType, useMaxValue, maxValue, theme }) {
  const { label, value } = row;
  const percentage = parseFloat(((value / total) * 100).toFixed(1));
  const barPercentage = useMaxValue
    ? parseFloat(((value / maxValue) * 100).toFixed(1))
    : percentage;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const toggle = () => {
    setExpanded(!expanded);
  };

  const handleSegmentTap = (e, segmentId) => {
    e.stopPropagation();
    setOpenTooltipId((prev) => (prev === segmentId ? null : segmentId));
  };

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (row?.rows) {
      let _rows = row.rows || [];
      _rows.sort((a, b) => b.value - a.value);
      setRows(_rows);
    }
  }, [row]);

  return (
    <ClickAwayListener onClickAway={() => setOpenTooltipId(null)}>
      <Box sx={{ mb: { xs: 2.5, sm: 2 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            mb: { xs: 1, sm: 0.75 }
          }}
          onClick={toggle}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: { xs: 1, sm: 0.75 },
              flexWrap: "wrap"
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                textDecoration: "none"
              }}
            >
              {label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({percentage}%) &bull; {value} {dataType}
            </Typography>
          </Box>
          <Box
            sx={{
              color: theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              ml: 1
            }}
          >
            {expanded ? <BCPAngleUp /> : <BCPAngleDown />}
          </Box>
        </Box>
        <BarTrack theme={theme} sx={{ width: `${barPercentage}%` }}>
          {rows.map((segment, i) => {
            segment.percentage = parseFloat(
              ((segment.value / total) * 100).toFixed(1)
            );
            const segmentWidth =
              row.value > 0 ? (segment.value / row.value) * 100 : 0;
            return (
              <Tooltip
                key={`segment-tooltip-${segment.id || i}`}
                title={
                  <SegmentHoverCard
                    segment={segment}
                    dataType={dataType}
                    color={getColor(i, rows.length)}
                    theme={theme}
                  />
                }
                open={
                  isMobile
                    ? openTooltipId === `segment-tooltip-${segment.id || i}`
                    : undefined
                }
                disableHoverListener={isMobile}
                disableFocusListener={isMobile}
                disableTouchListener
                componentsProps={{
                  tooltip: { sx: { backgroundColor: "transparent", p: 0 } }
                }}
              >
                <Box
                  onClick={
                    isMobile
                      ? (e) =>
                          handleSegmentTap(
                            e,
                            `segment-tooltip-${segment.id || i}`
                          )
                      : undefined
                  }
                  sx={{
                    height: "100%",
                    width: `${segmentWidth}%`,
                    backgroundColor: getColor(i, rows.length),
                    transition: "opacity 0.2s",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.8 }
                  }}
                />
              </Tooltip>
            );
          })}
        </BarTrack>

        <Collapse in={expanded}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1.5, sm: 2 },
              mt: { xs: 1.25, sm: 1 },
              pl: 0.5
            }}
          >
            {rows.map((segment, i) => {
              const segmentPercentage = parseFloat(
                ((segment.value / total) * 100).toFixed(1)
              );

              return (
                <Box
                  key={`expand-segment-${segment.id || i}`}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 0.5,
                      backgroundColor: getColor(i, rows.length),
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      textDecoration: "none",
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" }
                    }}
                  >
                    {segment.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({segmentPercentage}%)
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
}

export default function DashboardBarChart({ label, data, eventName }) {
  const { total, dataType, useMaxValue, nested } = data || {};
  const [rows, setRows] = useState(null);
  const [maxValue, setMaxValue] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportImage, setExportImage] = useState({
    dataUrl: null,
    blob: null
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const exportRef = useRef(null);
  const { captureElement, exporting } = useExportImage();

  const exportTitle = [eventName, label].filter(Boolean).join(" ");
  const displayTitle = isCapturing ? exportTitle : label;
  const exportFilename = `${slugifyFilename(exportTitle || "stats-export")}.png`;

  const [sortOrder, setSortOrder] = useState({
    type: "value",
    direction: "desc"
  });
  const toggleSort = () => {
    let _sortOrder = {
      type:
        sortOrder?.type === "label"
          ? "value"
          : sortOrder?.direction === "desc"
            ? "value"
            : "label",
      direction:
        sortOrder?.type === "label"
          ? "desc"
          : sortOrder?.direction === "desc"
            ? "asc"
            : "asc"
    };
    setSortOrder(_sortOrder);

    let _rows = [...rows];
    _rows = _rows.sort((a, b) => {
      if (_sortOrder.type === "label") {
        return a.label.localeCompare(b.label);
      }
      if (_sortOrder.direction === "desc") {
        return b.value - a.value;
      }
      return a.value - b.value;
    });
    setRows(_rows);
  };

  const theme = useTheme();

  const handleShare = async () => {
    if (!exportRef.current || exportLoading || exporting) return;

    setExportLoading(true);
    setExportImage({ dataUrl: null, blob: null });
    setShareOpen(true);
    flushSync(() => {
      setIsCapturing(true);
    });

    try {
      const backgroundColor =
        theme.palette.mode === "dark"
          ? theme.palette.background.default
          : theme.palette.background.paper;

      const { dataUrl, blob } = await captureElement(exportRef.current, {
        backgroundColor
      });
      setExportImage({ dataUrl, blob });
    } catch (err) {
      setShareOpen(false);
      postNotification("error", "Unable to export stats image");
    } finally {
      setIsCapturing(false);
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (data?.rows) {
      console.log("default sort");
      let _rows = data.rows || [];
      _rows = _rows.sort((a, b) => b.value - a.value);
      setMaxValue(_rows?.[0]?.value);
      setRows(_rows);
      setSortOrder({
        type: "value",
        direction: "desc"
      });
    }
  }, [data]);

  return (
    <>
      <Box
        ref={exportRef}
        sx={{
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : theme.palette.background.paper
        }}
      >
        {displayTitle?.length ? (
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              color: "primary",
              fontWeight: 600,
              textAlign: "center",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              pt: 2
            }}
          >
            {displayTitle}
          </Typography>
        ) : (
          <></>
        )}

        {rows?.length ? (
          <Box
            sx={{
              position: "relative",
              isolation: "isolate",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.02)",
              borderRadius: 1,
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: -1,
                opacity: 0.08,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Box
                component="img"
                src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/BannerLogo${
                  theme.palette.mode === "dark" ? "Dark" : ""
                }.png`}
                alt=""
                sx={{
                  width: { xs: 160, sm: 220, md: 260 },
                  height: "auto"
                }}
              />
            </Box>

            <Box
              data-export-hide
              sx={{
                position: "relative",
                height: nested ? "60px" : "48px"
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5
                }}
              >
                <Tooltip title="Share">
                  <IconButton
                    aria-label="share"
                    onClick={handleShare}
                    disabled={exportLoading || exporting}
                  >
                    <BCPShare />
                  </IconButton>
                </Tooltip>
                <IconButton aria-label="sort order" onClick={toggleSort}>
                  {sortOrder?.type === "label" ? (
                    <BCPSortAlphaDownAsc />
                  ) : sortOrder?.direction === "asc" ? (
                    <BCPSortAmountDownAsc />
                  ) : (
                    <BCPSortAmountDownDesc />
                  )}
                </IconButton>
              </Box>
            </Box>
            {rows.map((row, i) => {
              if (row.rows) {
                return (
                  <NestedRow
                    key={row.id || i}
                    row={row}
                    total={total}
                    useMaxValue={useMaxValue}
                    maxValue={maxValue}
                    dataType={dataType}
                    theme={theme}
                  />
                );
              }

              return (
                <SimpleRow
                  key={row.id || i}
                  row={row}
                  total={total}
                  useMaxValue={useMaxValue}
                  maxValue={maxValue}
                  dataType={dataType}
                  theme={theme}
                  color={getColor(i, rows.length)}
                />
              );
            })}
          </Box>
        ) : (
          <></>
        )}
      </Box>

      {shareOpen && (
        <ShareImageModal
          parentOpen={shareOpen}
          setParentOpen={setShareOpen}
          imageUrl={exportImage.dataUrl}
          imageBlob={exportImage.blob}
          filename={exportFilename}
          title={exportTitle || "Share Image"}
          loading={exportLoading || exporting}
        />
      )}
    </>
  );
}
